"use client";

import { supabase } from "@/utils/supabase/client";

interface LineItem {
  description: string;
  quantity: string;
  rate: number;
  total: string;
}

/**
 * Generate an invoice from recent billable time entries.
 * Returns the created invoice row or null if none created.
 */
export async function generateInvoiceFromRecentBillableTime(companyId: string) {
  if (!companyId) return null;

  const { data: billableLogs, error: logsError } = await supabase
    .from("time_logs")
    .select("*, clients(name, id), projects(name, id)")
    .eq("company_id", companyId)
    .eq("billable", true)
    .order("start_time", { ascending: false })
    .limit(10);

  if (logsError) throw logsError;
  if (!billableLogs || billableLogs.length === 0) {
    return null;
  }

  const grouped = billableLogs.reduce((acc: Record<string, any>, log: any) => {
    const key = `${log.client_id || "no-client"}-${log.project_id || "no-project"}`;
    if (!acc[key]) {
      acc[key] = {
        client_id: log.client_id,
        client_name: log.clients?.name || "Unassigned Client",
        project_id: log.project_id,
        project_name: log.projects?.name || "General Work",
        description: `Time logged for ${log.description || "Billable Work"}`,
        hours: 0,
        amount: 0,
      };
    }
    const hours = (log.duration_minutes || 0) / 60;
    acc[key].hours += hours;
    acc[key].amount += hours * 50; // £50/hr rate
    return acc;
  }, {});

  const lineItems: LineItem[] = Object.values(grouped).map((item: any) => ({
    description: item.description,
    quantity: item.hours.toFixed(1),
    rate: 50,
    total: item.amount.toFixed(2),
  }));

  const totalAmount = lineItems.reduce((sum: number, item: LineItem) => sum + parseFloat(item.total), 0);

  const { data: invoice, error: insertError } = await supabase
    .from("invoices")
    .insert({
      company_id: companyId,
      client_id: billableLogs[0]?.client_id || null,
      invoice_number: `AUTO-${Date.now()}`,
      total_amount: Math.round(totalAmount * 100) / 100,
      status: "draft",
      notes: `Auto-generated from ${billableLogs.length} billable time entries`,
      line_items: lineItems,
    })
    .select()
    .single();

  if (insertError) throw insertError;
  return invoice;
}